import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import * as React from 'react'
import { cn } from '@/lib/utils'

export interface CheckboxProps extends React.ComponentPropsWithoutRef<
  typeof CheckboxPrimitive.Root
> {
  className?: string
  indicatorClassName?: string
  indeterminate?: boolean
  variant?: 'primary' | 'secondary' | 'gray'
}

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(
  (
    { className, indicatorClassName, indeterminate = false, variant = 'secondary', ...props },
    ref,
  ) => {
    const checked = indeterminate ? 'indeterminate' : props.checked
    const filled = checked === true || checked === 'indeterminate'

    const filledClasses = {
      primary: 'bg-blue-9 border-blue-9',
      secondary: 'bg-gray-12 border-gray-12',
      gray: 'bg-gray-10 border-gray-10',
    }

    const filledHoverClasses = {
      primary: 'hover:bg-blue-8 hover:border-blue-8',
      secondary: 'hover:bg-gray-11 hover:border-gray-11',
      gray: 'hover:bg-gray-9 hover:border-gray-9',
    }

    const checkboxClasses = cn(
      'shrink-0 flex h-[14px] w-[14px] m-[1px] items-center justify-center rounded-[2px] border border-gray-6 bg-white text-white',
      !props.disabled && !filled && 'hover:border-gray-6 hover:bg-gray-1',
      !props.disabled && filled && filledHoverClasses[variant],
      filled && filledClasses[variant],
      props.disabled && 'pointer-events-none cursor-default border-gray-4 bg-gray-1',
      props.disabled && filled && 'bg-gray-4',
    )

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(checkboxClasses, className)}
        {...props}
        checked={checked}
        id={props.id || 'checkbox'}
      >
        <CheckboxPrimitive.Indicator
          className={cn(indicatorClassName, 'flex items-center justify-center text-white')}
        >
          {/* Show Minus if indeterminate */}
          {checked === 'indeterminate' ? (
            <Minus className="size-3" />
          ) : (
            <Check className="size-3" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    )
  },
)

Checkbox.displayName = 'Checkbox'
